import PropTypes from 'prop-types'
import { useMemo, useState } from 'react'
import { Box, Tab, Tabs, Typography } from '@mui/material'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { DOCUMENT_SCOPES } from '../../../generic/qr/qrDocumentResources'
import DocumentsCarouselView from '../../../configuration/document/DocumentsCarouselView'
import CenteredText from '../common/CenteredText'
import QrDocumentsTree from './QrDocumentsTree'
import { buildDocumentTree, flattenTree, initialNodeKey, nodeKey } from './qrDocumentTree'

/**
 * The documents of a scanned element, read-only.
 *
 * An element carries documents at two levels and the operator needs both: the reference level is
 * where the DRAWINGS live, the instance level is what belongs to this very lot or part. When Sun
 * returns both, they become tabs; with a single source the tabs are pointless and hidden.
 *
 * An order is the exception: it also carries its lines and their fabrication orders, a structure that
 * grows with the order. Tabs would run into dozens, so that case is picked from a tree instead — same
 * viewer, same rules, a different way in.
 *
 * Sun counts the documents of each level, so an empty one is known before asking for it: a level with
 * nothing in it says so instead of mounting a viewer that would paint a blank frame — which an
 * operator reads as "the drawing failed to load", not as "there is no drawing".
 *
 * The viewer is remounted on every source change (`key`) because DocumentsCarouselView keeps the
 * selected document in its own state, and carrying index 3 of the drawings over to a source with one
 * certificate would show the operator a blank frame.
 */

const SCOPE_LABELS = { REFERENCE: 'Reference', INSTANCE: 'Instance' }

const QrDocumentsPanel = ({ sources = [], orderLines = [], preferredScope = DOCUMENT_SCOPES.REFERENCE }) => {
  const { t_ } = useSmartTranslation()

  const nodes = useMemo(() => buildDocumentTree(sources, orderLines), [sources, orderLines])
  const pickable = useMemo(() => flattenTree(nodes), [nodes])
  const [selectedKey, setSelectedKey] = useState(() => initialNodeKey(nodes, preferredScope))

  const selected = pickable.find((node) => node.key === selectedKey) ?? pickable[0]

  if (!selected) {
    return (
      <CenteredText>
        <Typography variant='body2' color='text.secondary'>{t_('No documents for this element')}</Typography>
      </CenteredText>
    )
  }

  // A scanned order brings a structure to walk; everything else brings at most two levels.
  const asTree = orderLines.length > 0

  // Only ORDER reaches this, and only without a structure: it has no reference level, so the operator
  // gets the element's own documents instead of an error, with a note explaining what is being shown.
  const missingPreferredScope = !asTree &&
    preferredScope === DOCUMENT_SCOPES.REFERENCE &&
    !sources.some((source) => source.scope === DOCUMENT_SCOPES.REFERENCE)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0, width: '100%' }}>
      {!asTree && sources.length > 1 && (
        <Tabs
          value={selectedKey}
          onChange={(event, next) => setSelectedKey(next)}
          variant='scrollable'
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
        >
          {sources.map((source) => (
            <Tab
              key={nodeKey(source)}
              value={nodeKey(source)}
              disabled={source.documentCount === 0}
              label={t_(SCOPE_LABELS[source.scope] ?? source.scope) + ' (' + source.documentCount + ')'}
            />
          ))}
        </Tabs>
      )}

      {missingPreferredScope && (
        <Typography variant='caption' color='text.secondary' sx={{ textAlign: 'left', py: 0.5, flexShrink: 0 }}>
          {t_('This label has no reference level; showing the documents of the element itself')}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0, width: '100%' }}>
        {asTree && (
          <QrDocumentsTree nodes={nodes} selectedKey={selectedKey} onSelect={setSelectedKey} />
        )}

        {/* minWidth 0 so the viewer yields to the tree instead of pushing it out of the dialog: a
            carousel of drawings has no natural width to negotiate with. */}
        <Box sx={{ display: 'flex', flexGrow: 1, minWidth: 0, minHeight: 0 }}>
          {selected.documentCount === 0
            ? (
              <CenteredText>
                <Typography variant='body2' color='text.secondary'>{t_('No documents at this level')}</Typography>
              </CenteredText>
              )
            : (
              <DocumentsCarouselView
                key={selected.key}
                resourceType={selected.resourceType}
                referenceId={selected.referenceId}
                showDocumentsHeader
                enableUpload={false}
                enableReorder={false}
                canManageMainImage={false}
              />
              )}
        </Box>
      </Box>
    </Box>
  )
}

QrDocumentsPanel.propTypes = {
  /** Document levels of the scanned element, as normalised by qrLookupApi. */
  sources: PropTypes.arrayOf(PropTypes.shape({
    scope: PropTypes.string,
    resourceType: PropTypes.string,
    referenceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    documentCount: PropTypes.number
  })),
  /** Lines of a scanned order with their fabrication orders; empty for every other type. */
  orderLines: PropTypes.array,
  /** Which scope to open first when it is available. */
  preferredScope: PropTypes.string
}

export default QrDocumentsPanel
